import React, { useState, useRef } from 'react';
import { X, Upload, Download, CheckCircle, AlertCircle, AlertTriangle } from 'lucide-react';
import { post } from '../services/api';

export interface CsvColumn {
  key: string;
  label: string;
  required?: boolean;
  hint?: string;
}

interface Props {
  title: string;
  endpoint: string;          // 例: /users/bulk-import
  bodyKey: string;           // 例: "users"
  columns: CsvColumn[];
  templateRows: string[][];  // テンプレートのサンプル行
  onClose: () => void;
  onComplete: () => void;
}

function parseCsv(text: string): string[][] {
  // BOM除去
  const clean = text.replace(/^﻿/, '');
  return clean.split(/\r?\n/).filter(l => l.trim()).map(line => {
    const cols: string[] = [];
    let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
        else inQ = !inQ;
      } else if (c === ',' && !inQ) {
        cols.push(cur); cur = '';
      } else {
        cur += c;
      }
    }
    cols.push(cur);
    return cols;
  });
}

type Phase = 'upload' | 'preview' | 'result';

const CsvImportModal: React.FC<Props> = ({
  title, endpoint, bodyKey, columns, templateRows, onClose, onComplete,
}) => {
  const [phase, setPhase]       = useState<Phase>('upload');
  const [rows, setRows]         = useState<Record<string, string>[]>([]);
  const [parseError, setParseError] = useState('');
  const [importing, setImporting]   = useState(false);
  const [result, setResult]         = useState<{ created: number; errors: { row: number; message: string }[] } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const downloadTemplate = () => {
    const header = columns.map(c => c.label).join(',');
    const sample = templateRows.map(r => r.join(',')).join('\r\n');
    const csv = '﻿' + header + '\r\n' + sample;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${title}_テンプレート.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = (file: File) => {
    setParseError('');
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const text = e.target?.result as string;
        const parsed = parseCsv(text);
        if (parsed.length < 2) { setParseError('データ行がありません（ヘッダーのみ）'); return; }
        const header = parsed[0]!;
        const dataRows = parsed.slice(1).map(r => {
          const obj: Record<string, string> = {};
          columns.forEach((col, i) => { obj[col.key] = r[i]?.trim() ?? ''; });
          return obj;
        });
        if (dataRows.length > 500) { setParseError('一度に登録できるのは500件までです'); return; }
        setRows(dataRows);
        setPhase('preview');
      } catch { setParseError('CSVの解析に失敗しました'); }
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await post<{ data: { created: number; errors: { row: number; message: string }[] } }>(
        endpoint, { [bodyKey]: rows }
      );
      setResult(res.data);
      setPhase('result');
      if (res.data.created > 0) onComplete();
    } catch (err: any) {
      setParseError(err.message ?? 'インポートに失敗しました');
    } finally {
      setImporting(false);
    }
  };

  const S = {
    overlay: { position: 'fixed' as const, inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    modal: { background: '#fff', borderRadius: 16, width: 720, maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' as const, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
    header: { padding: '18px 24px', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    body: { padding: 24, overflowY: 'auto' as const, flex: 1 },
    footer: { padding: '14px 24px', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: 10 },
  };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        {/* ヘッダー */}
        <div style={S.header}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{title} CSVインポート</div>
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
              {phase === 'upload' ? 'CSVファイルをアップロードして一括登録' :
               phase === 'preview' ? `${rows.length}件のデータを確認してください` :
               'インポート完了'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        <div style={S.body}>

          {/* ─── アップロードフェーズ ─── */}
          {phase === 'upload' && (
            <>
              {/* テンプレートDL */}
              <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 10, padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#0369A1' }}>① まずテンプレートをダウンロード</div>
                  <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3 }}>
                    必須列: {columns.filter(c => c.required).map(c => c.label).join('・')}
                  </div>
                </div>
                <button onClick={downloadTemplate} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
                  background: '#0284C7', color: '#fff', border: 'none', borderRadius: 8,
                  fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                }}>
                  <Download size={14} /> テンプレートDL
                </button>
              </div>

              {/* 列の説明 */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>列の説明</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#F9FAFB' }}>
                      {['列名', '必須', '説明'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#6B7280', fontWeight: 600, borderBottom: '1px solid #E5E7EB' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {columns.map(col => (
                      <tr key={col.key} style={{ borderBottom: '1px solid #F3F4F6' }}>
                        <td style={{ padding: '7px 12px', fontWeight: 600 }}>{col.label}</td>
                        <td style={{ padding: '7px 12px' }}>
                          {col.required
                            ? <span style={{ color: '#DC2626', fontWeight: 700 }}>必須</span>
                            : <span style={{ color: '#9CA3AF' }}>任意</span>}
                        </td>
                        <td style={{ padding: '7px 12px', color: '#6B7280' }}>{col.hint ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ファイルドロップ */}
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#0066CC'; }}
                onDragLeave={e => { e.currentTarget.style.borderColor = '#D1D5DB'; }}
                onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#D1D5DB'; const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
                style={{
                  border: '2px dashed #D1D5DB', borderRadius: 12, padding: '32px 20px',
                  textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.15s',
                }}
              >
                <Upload size={32} color="#9CA3AF" style={{ margin: '0 auto 10px' }} />
                <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>② CSVファイルをドロップまたはクリックして選択</div>
                <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>UTF-8 または Shift-JIS、最大500件</div>
                <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }} />
              </div>

              {parseError && (
                <div style={{ marginTop: 12, padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, color: '#DC2626', fontSize: 13, display: 'flex', gap: 8 }}>
                  <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />{parseError}
                </div>
              )}
            </>
          )}

          {/* ─── プレビューフェーズ ─── */}
          {phase === 'preview' && (
            <>
              <div style={{ marginBottom: 14, fontSize: 13, color: '#374151' }}>
                内容を確認して「インポート実行」を押してください。
              </div>
              <div style={{ overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#F9FAFB' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', color: '#6B7280', fontWeight: 600, borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap' }}>行</th>
                      {columns.map(c => (
                        <th key={c.key} style={{ padding: '8px 12px', textAlign: 'left', color: '#6B7280', fontWeight: 600, borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap' }}>
                          {c.label}{c.required && <span style={{ color: '#DC2626' }}>*</span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 100).map((row, i) => {
                      const missing = columns.filter(c => c.required && !row[c.key]);
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #F3F4F6', background: missing.length ? '#FFF5F5' : i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                          <td style={{ padding: '7px 12px', color: '#9CA3AF' }}>{i + 2}</td>
                          {columns.map(c => (
                            <td key={c.key} style={{ padding: '7px 12px', color: !row[c.key] && c.required ? '#DC2626' : '#111827' }}>
                              {row[c.key] || <span style={{ color: '#D1D5DB' }}>（空）</span>}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {rows.length > 100 && <div style={{ marginTop: 8, fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>（先頭100件を表示中 / 全{rows.length}件）</div>}
              {parseError && (
                <div style={{ marginTop: 12, padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, color: '#DC2626', fontSize: 13 }}>
                  {parseError}
                </div>
              )}
            </>
          )}

          {/* ─── 結果フェーズ ─── */}
          {phase === 'result' && result && (
            <>
              <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                <div style={{ flex: 1, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '16px 20px', textAlign: 'center' }}>
                  <CheckCircle size={28} color="#16A34A" style={{ margin: '0 auto 6px' }} />
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#16A34A' }}>{result.created}</div>
                  <div style={{ fontSize: 13, color: '#16A34A', fontWeight: 600 }}>件 登録成功</div>
                </div>
                {result.errors.length > 0 && (
                  <div style={{ flex: 1, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '16px 20px', textAlign: 'center' }}>
                    <AlertTriangle size={28} color="#DC2626" style={{ margin: '0 auto 6px' }} />
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#DC2626' }}>{result.errors.length}</div>
                    <div style={{ fontSize: 13, color: '#DC2626', fontWeight: 600 }}>件 エラー</div>
                  </div>
                )}
              </div>
              {result.errors.length > 0 && (
                <>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>エラー詳細</div>
                  <div style={{ border: '1px solid #FECACA', borderRadius: 8, overflow: 'hidden' }}>
                    {result.errors.map((e, i) => (
                      <div key={i} style={{ padding: '9px 14px', borderBottom: i < result.errors.length - 1 ? '1px solid #FEE2E2' : 'none', fontSize: 13, display: 'flex', gap: 12, background: '#FFF5F5' }}>
                        <span style={{ color: '#9CA3AF', flexShrink: 0 }}>{e.row}行目</span>
                        <span style={{ color: '#DC2626' }}>{e.message}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* フッター */}
        <div style={S.footer}>
          {phase === 'upload' && (
            <button onClick={onClose} className="btn btn-secondary">キャンセル</button>
          )}
          {phase === 'preview' && (
            <>
              <button onClick={() => { setPhase('upload'); setRows([]); setParseError(''); }} className="btn btn-secondary">戻る</button>
              <button onClick={handleImport} disabled={importing || rows.length === 0} style={{
                padding: '9px 24px', background: importing ? '#9CA3AF' : '#0066CC',
                color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
                cursor: importing ? 'not-allowed' : 'pointer',
              }}>
                {importing ? 'インポート中...' : `${rows.length}件をインポート`}
              </button>
            </>
          )}
          {phase === 'result' && (
            <button onClick={onClose} style={{ padding: '9px 24px', background: '#0066CC', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              閉じる
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CsvImportModal;
